export type BscPerspective = 'Financial' | 'Customer' | 'Internal Process' | 'Learning & Growth'

export const BSC_ROW_ORDER: BscPerspective[] = [
  'Financial',
  'Customer',
  'Internal Process',
  'Learning & Growth',
]

export type ThemeLabel = 'Productivity' | 'Growth'

export const CVP_ATTRIBUTES = [
  'Price', 'Quality', 'Availability', 'Selection', 'Functionality',
  'Service', 'Partnership', 'Brand', 'Image',
] as const

export const IP_CLUSTERS = [
  'Operations Management',
  'Customer Management',
  'Innovation',
  'Regulatory & Social',
] as const
export type IpCluster = typeof IP_CLUSTERS[number]

export const LG_CAPITALS = [
  'Human Capital',
  'Information Capital',
  'Organization Capital',
] as const
export type LgCapital = typeof LG_CAPITALS[number]

export interface CanvasMetric {
  id: string
  name: string
  formula?: string
  target?: { value: string; unit?: string; horizon?: string }
  suggestedTarget?: { value: string; unit?: string; horizon?: string }
}

export interface CanvasTactic {
  id: string
  title: string
  metrics: CanvasMetric[]
}

export interface CanvasObjective {
  id: string
  title: string
  perspective: BscPerspective
  tactics: CanvasTactic[]
  theme?: ThemeLabel
  cvpAttributes?: string[]
  ipCluster?: IpCluster
  lgCapital?: LgCapital
}

export type CausalLinkStrength = 'HIGH' | 'MEDIUM' | 'LOW'

export interface CanvasCausalLink {
  source: string
  target: string
  relationship?: string
  strength?: CausalLinkStrength
}

export type CascadeViewMode = 'filtered' | 'all'

/** Map an objective_id prefix (F/C/P/L) to its BSC perspective. */
export function derivePerspectiveFromId(id: string | undefined | null): BscPerspective | null {
  if (!id || typeof id !== 'string') return null
  switch (id[0]) {
    case 'F': return 'Financial'
    case 'C': return 'Customer'
    case 'P': return 'Internal Process'
    case 'L': return 'Learning & Growth'
    default: return null
  }
}

/**
 * Render-subset filter for the Canvas cascade overlay.
 *   - 'all' returns the input unchanged.
 *   - 'filtered' keeps only links with strength === 'HIGH' AND source/target in
 *     different perspectives. Links missing strength are treated as MEDIUM
 *     (i.e. filtered out by default).
 */
export function filterCausalLinks(
  links: CanvasCausalLink[],
  mode: CascadeViewMode,
): CanvasCausalLink[] {
  if (mode === 'all') return links
  return links.filter(l => {
    if (l.strength !== 'HIGH') return false
    const sp = derivePerspectiveFromId(l.source)
    const tp = derivePerspectiveFromId(l.target)
    if (!sp || !tp) return false
    return sp !== tp
  })
}

export interface BscRow {
  perspective: BscPerspective
  objectives: CanvasObjective[]
}

function normalizePerspective(p: string | undefined): BscPerspective {
  if (!p) return 'Internal Process'
  if (p === 'Financial' || p === 'Customer' || p === 'Learning & Growth') return p
  if (p === 'Internal Process') return p
  // Tolerate variants
  const lower = p.toLowerCase()
  if (lower.includes('financial') || lower.includes('cost') || lower.includes('revenue')) return 'Financial'
  if (lower.includes('customer') || lower.includes('patient')) return 'Customer'
  if (lower.includes('learning') || lower.includes('growth') || lower.includes('staff')) return 'Learning & Growth'
  return 'Internal Process'
}

export function extractObjectives(strategyMap: any): CanvasObjective[] {
  if (!strategyMap) return []

  // Priority 1: perspectives[] — produced by transformStrategyMap in ChatPanel
  // (pillars[] -> perspectives[] with nested objectives[]). For live
  // conversations the strategyMap typically carries BOTH shapes — perspectives
  // for layout, pillars for structural detail (tactics + metrics). Enrich
  // perspectives-derived objectives with their matching pillar's tactics so
  // downstream consumers (e.g. MeasuresInitiativesRegister) see the full data.
  if (Array.isArray(strategyMap.perspectives) && strategyMap.perspectives.length > 0) {
    const pillarByObjId = new Map<string, any>()
    if (Array.isArray(strategyMap.pillars)) {
      for (const pillar of strategyMap.pillars) {
        if (pillar?.objective_id) pillarByObjId.set(pillar.objective_id, pillar)
      }
    }

    const out: CanvasObjective[] = []
    for (const p of strategyMap.perspectives) {
      const perspectiveName = p?.perspective_name ?? p?.name
      for (const obj of p?.objectives ?? []) {
        const id = obj.objective_id ?? obj.id ?? ''
        const pillar = pillarByObjId.get(id)
        const tactics: CanvasTactic[] = Array.isArray(pillar?.tactics)
          ? pillar.tactics.map((t: any) => ({
              id: t.tactic_id ?? t.initiative_id ?? '',
              title: t.title ?? '',
              metrics: Array.isArray(t.metrics)
                ? t.metrics.map((m: any) => ({
                    id: m.id ?? '',
                    name: m.name ?? '',
                    formula: m.formula,
                    target: m.target,
                    suggestedTarget: m.suggested_target,
                  }))
                : [],
            }))
          : []
        out.push({
          id,
          title: obj.objective_name ?? obj.title ?? obj.name ?? '',
          perspective: normalizePerspective(perspectiveName),
          tactics,
          theme: normalizeTheme(pillar?.theme),
          cvpAttributes: normalizeCvpAttributes(pillar?.cvp_attributes),
          ipCluster: normalizeIpCluster(pillar?.ip_cluster),
          lgCapital: normalizeLgCapital(pillar?.lg_capital),
        })
      }
    }
    return out
  }

  // Priority 2: flat objectives[]
  if (Array.isArray(strategyMap.objectives) && strategyMap.objectives.length > 0) {
    return strategyMap.objectives.map((obj: any) => ({
      id: obj.id ?? obj.objective_id ?? '',
      title: obj.title ?? obj.name ?? '',
      perspective: normalizePerspective(obj.perspective ?? obj.bsc_perspective),
      tactics: [],
    }))
  }

  // Priority 3: legacy pillars[]
  if (Array.isArray(strategyMap.pillars) && strategyMap.pillars.length > 0) {
    return strategyMap.pillars.map((pillar: any) => ({
      id: pillar.objective_id ?? '',
      title: pillar.name ?? '',
      perspective: normalizePerspective(pillar.bsc_perspective),
      tactics: Array.isArray(pillar.tactics)
        ? pillar.tactics.map((t: any) => ({
            id: t.tactic_id ?? t.initiative_id ?? '',
            title: t.title ?? '',
            metrics: Array.isArray(t.metrics)
              ? t.metrics.map((m: any) => ({
                  id: m.id ?? '',
                  name: m.name ?? '',
                  formula: m.formula,
                  target: m.target,
                  suggestedTarget: m.suggested_target,
                }))
              : [],
          }))
        : [],
      theme: normalizeTheme(pillar.theme),
      cvpAttributes: normalizeCvpAttributes(pillar.cvp_attributes),
      ipCluster: normalizeIpCluster(pillar.ip_cluster),
      lgCapital: normalizeLgCapital(pillar.lg_capital),
    }))
  }

  return []
}

export function groupByBscRow(objectives: CanvasObjective[]): BscRow[] {
  return BSC_ROW_ORDER.map(p => ({
    perspective: p,
    objectives: objectives.filter(o => o.perspective === p),
  }))
}

export function dedupCausalLinks(strategyMap: any): CanvasCausalLink[] {
  if (!strategyMap) return []
  const seen = new Map<string, CanvasCausalLink>()

  if (Array.isArray(strategyMap.causal_links)) {
    for (const link of strategyMap.causal_links) {
      // transformStrategyMap writes causal_chains into causal_links, preserving
      // the from_/to_objective_id field names — so accept both shapes here.
      const source = link.source_objective_id ?? link.from_objective_id ?? ''
      const target = link.target_objective_id ?? link.to_objective_id ?? ''
      if (!source || !target) continue
      const key = `${source}->${target}`
      seen.set(key, {
        source,
        target,
        relationship: link.relationship,
        strength: link.strength as CausalLinkStrength | undefined,
      })
    }
  }

  if (Array.isArray(strategyMap.causal_chains)) {
    for (const chain of strategyMap.causal_chains) {
      // Accept both naming conventions (some payloads use source_/target_,
      // others use from_/to_).
      const source = chain.source_objective_id ?? chain.from_objective_id ?? ''
      const target = chain.target_objective_id ?? chain.to_objective_id ?? ''
      if (!source || !target) continue
      const key = `${source}->${target}`
      if (!seen.has(key)) {
        seen.set(key, {
          source,
          target,
          strength: chain.strength as CausalLinkStrength | undefined,
        })
      }
    }
  }

  return Array.from(seen.values())
}

/** Flatten all metrics across an objective's tactics, deduped by `id || name`. */
export function flatMeasures(tactics: CanvasTactic[]): CanvasMetric[] {
  const seen = new Set<string>()
  const out: CanvasMetric[] = []
  for (const t of tactics) {
    for (const m of t.metrics) {
      const key = m.id || m.name
      if (!key || seen.has(key)) continue
      seen.add(key)
      out.push(m)
    }
  }
  return out
}

function normalizeTheme(value: unknown): ThemeLabel | undefined {
  if (value === 'Productivity' || value === 'Growth') return value
  return undefined
}

function normalizeCvpAttributes(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  const vocab = new Set<string>(CVP_ATTRIBUTES)
  return value.filter((v): v is string => typeof v === 'string' && vocab.has(v))
}

function normalizeIpCluster(value: unknown): IpCluster | undefined {
  if (typeof value !== 'string') return undefined
  return (IP_CLUSTERS as readonly string[]).includes(value) ? (value as IpCluster) : undefined
}

function normalizeLgCapital(value: unknown): LgCapital | undefined {
  if (typeof value !== 'string') return undefined
  return (LG_CAPITALS as readonly string[]).includes(value) ? (value as LgCapital) : undefined
}

export interface ThemePartition {
  productivity: CanvasObjective[]
  growth: CanvasObjective[]
  unclassified: CanvasObjective[]
}

/** Split a row's objectives by theme. Used by RowSlot to decide single-vs-two-column rendering. */
export function partitionByTheme(objectives: CanvasObjective[]): ThemePartition {
  const productivity: CanvasObjective[] = []
  const growth: CanvasObjective[] = []
  const unclassified: CanvasObjective[] = []
  for (const obj of objectives) {
    if (obj.theme === 'Productivity') productivity.push(obj)
    else if (obj.theme === 'Growth') growth.push(obj)
    else unclassified.push(obj)
  }
  return { productivity, growth, unclassified }
}

export interface ClusterPartition {
  byCluster: Record<IpCluster, CanvasObjective[]>
  unclassified: CanvasObjective[]
}

/** Split Internal-Process objectives by ipCluster. All 4 cluster keys present even when empty. */
export function partitionByCluster(objectives: CanvasObjective[]): ClusterPartition {
  const byCluster: Record<IpCluster, CanvasObjective[]> = {
    'Operations Management': [],
    'Customer Management': [],
    'Innovation': [],
    'Regulatory & Social': [],
  }
  const unclassified: CanvasObjective[] = []
  for (const obj of objectives) {
    if (obj.ipCluster && obj.ipCluster in byCluster) {
      byCluster[obj.ipCluster].push(obj)
    } else {
      unclassified.push(obj)
    }
  }
  return { byCluster, unclassified }
}

export interface CapitalPartition {
  byCapital: Record<LgCapital, CanvasObjective[]>
  unclassified: CanvasObjective[]
}

/** Split Learning & Growth objectives by lgCapital. All 3 capital keys present even when empty. */
export function partitionByCapital(objectives: CanvasObjective[]): CapitalPartition {
  const byCapital: Record<LgCapital, CanvasObjective[]> = {
    'Human Capital': [],
    'Information Capital': [],
    'Organization Capital': [],
  }
  const unclassified: CanvasObjective[] = []
  for (const obj of objectives) {
    if (obj.lgCapital && obj.lgCapital in byCapital) {
      byCapital[obj.lgCapital].push(obj)
    } else {
      unclassified.push(obj)
    }
  }
  return { byCapital, unclassified }
}

// ===== Phase v2: canonical constants + aggregation helpers =====

/**
 * Canonical Kaplan-Norton perspective questions, displayed under each
 * perspective label on the Canvas. Static — same for every company.
 */
export const BSC_PERSPECTIVE_DESCRIPTIONS: Record<BscPerspective, string> = {
  'Financial':
    'To succeed financially, how should we appear to our shareholders?',
  'Customer':
    'To achieve our vision, how should we appear to our customers?',
  'Internal Process':
    'To satisfy our shareholders and customers, what business processes must we excel at?',
  'Learning & Growth':
    'To achieve our vision, how will we sustain our ability to change and improve?',
}

/**
 * Display-only mapping for Internal Process cluster names — uses fuller
 * phrasing matching the reference image. Enum values remain unchanged
 * so existing classification data stays valid.
 */
export const IP_CLUSTER_DISPLAY: Record<IpCluster, string> = {
  'Operations Management': 'Operations Management Processes',
  'Customer Management': 'Customer Management Processes',
  'Innovation': 'Innovation Processes',
  'Regulatory & Social': 'Regulatory and Social Processes',
}

export interface PriorityCell {
  name: string
  description: string
  strategic_result?: string
}

export interface KpiLine {
  name: string
  target: string
}

export interface PerspectiveScorecard {
  measures: string[]
  targets: string[]
  initiatives: string[]
}

/**
 * Convert MECE pillars into the top-N priority cells for the Canvas header.
 * Pillars are sorted by priority_rank ascending (missing rank treated as 999).
 */
export function meceToPriorities(
  meceTable: {
    pillars?: Array<{
      name?: string
      description?: string
      priority_rank?: number
      strategic_result?: string
    }>
  } | undefined | null,
  n: number,
): PriorityCell[] {
  if (!meceTable?.pillars?.length) return []
  const sorted = [...meceTable.pillars].sort(
    (a, b) => (a.priority_rank ?? 999) - (b.priority_rank ?? 999),
  )
  return sorted.slice(0, n).map(p => ({
    name: p.name ?? '',
    description: p.description ?? '',
    strategic_result: p.strategic_result,
  }))
}

function formatTarget(target?: { value: string; unit?: string }): string {
  if (!target?.value) return '—'
  return target.unit ? `${target.value} ${target.unit}` : target.value
}

/**
 * Flatten all metrics across all tactics in all pillars, sort by tactic
 * priority, dedupe by metric name, return top N.
 */
export function topKpisFromMece(
  meceTable: {
    pillars?: Array<{
      tactics?: Array<{
        priority?: number
        metrics?: Array<{ name?: string; target?: { value: string; unit?: string } }>
      }>
    }>
  } | undefined | null,
  n: number,
): KpiLine[] {
  if (!meceTable?.pillars?.length) return []
  const flat: Array<{ priority: number; name: string; target: string }> = []
  for (const pillar of meceTable.pillars) {
    for (const tactic of pillar.tactics ?? []) {
      for (const metric of tactic.metrics ?? []) {
        if (!metric.name) continue
        flat.push({
          priority: tactic.priority ?? 999,
          name: metric.name,
          target: formatTarget(metric.target),
        })
      }
    }
  }
  flat.sort((a, b) => a.priority - b.priority)
  const seen = new Set<string>()
  const out: KpiLine[] = []
  for (const f of flat) {
    if (seen.has(f.name)) continue
    seen.add(f.name)
    out.push({ name: f.name, target: f.target })
    if (out.length >= n) break
  }
  return out
}

/**
 * For each of the top-N priorities (by pillar.priority_rank), return the
 * top KPI for that priority's pillar (selected by lowest tactic priority,
 * first metric on that tactic). Returns array of length N with null for
 * priorities that have no metrics.
 */
export function topKpiPerPriority(
  meceTable: {
    pillars?: Array<{
      priority_rank?: number
      tactics?: Array<{
        priority?: number
        metrics?: Array<{ name?: string; target?: { value: string; unit?: string } }>
      }>
    }>
  } | undefined | null,
  n: number,
): Array<KpiLine | null> {
  if (!meceTable?.pillars?.length) return Array(n).fill(null)
  const sorted = [...meceTable.pillars].sort(
    (a, b) => (a.priority_rank ?? 999) - (b.priority_rank ?? 999),
  )
  const out: Array<KpiLine | null> = []
  for (let i = 0; i < n; i++) {
    const pillar = sorted[i]
    if (!pillar?.tactics?.length) {
      out.push(null)
      continue
    }
    const tacticsSorted = [...pillar.tactics].sort(
      (a, b) => (a.priority ?? 999) - (b.priority ?? 999),
    )
    let picked: KpiLine | null = null
    for (const tactic of tacticsSorted) {
      const metric = (tactic.metrics ?? []).find(m => m.name)
      if (metric?.name) {
        picked = { name: metric.name, target: formatTarget(metric.target) }
        break
      }
    }
    out.push(picked)
  }
  return out
}

/**
 * For each BSC perspective, aggregate the deduped list of measures (with
 * matching targets at the same index) and initiatives from objectives'
 * tactics. Used by per-perspective scorecard cells.
 */
export function aggregateByPerspective(
  objectives: CanvasObjective[],
): Record<BscPerspective, PerspectiveScorecard> {
  const out: Record<BscPerspective, PerspectiveScorecard> = {
    'Financial': { measures: [], targets: [], initiatives: [] },
    'Customer': { measures: [], targets: [], initiatives: [] },
    'Internal Process': { measures: [], targets: [], initiatives: [] },
    'Learning & Growth': { measures: [], targets: [], initiatives: [] },
  }
  for (const obj of objectives) {
    const sc = out[obj.perspective]
    for (const tactic of obj.tactics) {
      if (!sc.initiatives.includes(tactic.title)) {
        sc.initiatives.push(tactic.title)
      }
      for (const metric of tactic.metrics) {
        if (sc.measures.includes(metric.name)) continue
        sc.measures.push(metric.name)
        sc.targets.push(formatTarget(metric.target))
      }
    }
  }
  return out
}
